import { useState } from "react";
import { IconEye, IconFileExport, IconTrash } from "@tabler/icons-react";
import styles from "./DocumentSelectionBar.module.css";
import Button from "../UI/Button";
import Text from "../UI/Text";
import Callout from "../UI/Callout";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import CountBadge from "../UI/CountBadge";
import type { DocumentFile } from "../../models/document";
// @ts-expect-error pure js
import { SignatureContratStatus } from "../../models/signatureElectronique";

interface DocumentSelectionBarProps {
  files: DocumentFile[];
  onClear: () => void;
  onDownloadZip: (files: DocumentFile[]) => void;
  onShare?: (files: DocumentFile[]) => Promise<void>;
  /** Résout `true` quand la suppression a abouti ; la modale se ferme alors. */
  onDelete?: (files: DocumentFile[]) => Promise<boolean>;
  profileBailleurId?: number;
}

function fileLabel(file: DocumentFile) {
  return file.filename ?? `Document n°${file.id}`;
}

/**
 * Barre d'actions flottante de la sélection multiple de la vue liste. La
 * suppression demande confirmation en listant les fichiers concernés.
 */
export default function DocumentSelectionBar({
  files,
  onClear,
  onDownloadZip,
  onShare,
  onDelete,
  profileBailleurId,
}: DocumentSelectionBarProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  if (files.length === 0) return null;

  const count = files.length;
  const hasSignedFile = files.some(
    (file) =>
      file.signature_electronique_status ===
      SignatureContratStatus.Archived.status,
  );
  const toShare = files.filter((file) => !file.shared);

  async function deleteHandler() {
    setIsDeleting(true);
    try {
      if (await onDelete?.(files)) setConfirmOpen(false);
    } finally {
      setIsDeleting(false);
    }
  }

  async function shareHandler() {
    setIsSharing(true);
    try {
      await onShare?.(toShare);
    } finally {
      setIsSharing(false);
    }
  }

  return (
    <>
      <div
        role="region"
        aria-label="Actions sur la sélection"
        className={styles.bar}
      >
        <Text as="div" weight="bold" className={styles.count}>
          <CountBadge count={count} />
          {count > 1 ? "documents sélectionnés" : "document sélectionné"}
        </Text>
        <Button
          variant="secondary"
          icon={IconFileExport}
          onClick={() => onDownloadZip(files)}
        >
          <Text as="span" variant="deci" className={styles.label}>
            Télécharger (ZIP)
          </Text>
        </Button>
        {onShare && (
          <Button
            variant="secondary"
            icon={IconEye}
            isLoading={isSharing}
            disabled={toShare.length === 0}
            requiresWrite
            profileId={profileBailleurId}
            onClick={shareHandler}
          >
            <Text as="span" variant="deci" className={styles.label}>
              Partager au locataire
            </Text>
          </Button>
        )}
        {onDelete && (
          <Button
            variant="danger"
            icon={IconTrash}
            requiresWrite
            profileId={profileBailleurId}
            onClick={() => setConfirmOpen(true)}
          >
            <Text as="span" variant="deci" className={styles.label}>
              Supprimer
            </Text>
          </Button>
        )}
        <Button variant="tertiary" onClick={onClear}>
          Annuler
        </Button>
      </div>

      <ModalDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        size="sm"
        header={`Supprimer ${count} document${count > 1 ? "s" : ""} ?`}
      >
        <ModalDialogBody className={styles.confirmBody}>
          <Text>
            Ces fichiers seront définitivement supprimés. Vous ne pourrez pas
            revenir en arrière.
          </Text>
          <ul className={styles.fileList}>
            {files.map((file) => (
              <Text as="li" key={file.id} variant="deci">
                {fileLabel(file)}
              </Text>
            ))}
          </ul>
          {hasSignedFile && (
            <Callout variant="warning">
              La sélection contient un document signé électroniquement. Sa
              preuve de signature ne sera plus téléchargeable depuis Qalimo.
            </Callout>
          )}
        </ModalDialogBody>
        <ModalDialogFooter>
          <Button
            variant="secondary"
            type="button"
            onClick={() => setConfirmOpen(false)}
          >
            Annuler
          </Button>
          <Button
            variant="danger"
            type="button"
            isLoading={isDeleting}
            onClick={deleteHandler}
          >
            Supprimer définitivement
          </Button>
        </ModalDialogFooter>
      </ModalDialog>
    </>
  );
}
